import { useContext, useEffect, useState } from 'react'
import Logo from '../../assets/images/logo.png'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Preloader from '../../whatsapp/small_component/preloader';
import Swalaleart from '../../whatsapp/function/swalaleart';
import { ToastContainer } from 'react-toastify';
import { faEnvelope, faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { faLock, faUser, faCheck } from '@fortawesome/free-solid-svg-icons';
import { ThemeContext } from '../../routing/ApplicationRouting';
import domain from '../../reusecomponent/domain';
import './ForgotPassword.css';

export default function Forgotpassword() {
    const navigate = useNavigate();
    const [loder, setloder] = useState(false);
    const [currentStep, setCurrentStep] = useState(1); // 1: email, 2: OTP, 3: new password
    const [email, setEmail] = useState('');
    const [userRole, setUserRole] = useState('Owner'); // Store user role from email step
    const [otp, setOtp] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [resetToken, setResetToken] = useState(''); // Store the token after OTP verification
    const theme = useContext(ThemeContext);

    // Add login-page class for consistent auth page styling and set GMB theme colors
    useEffect(() => {
        document.body.classList.add('login-page');

        // Set GMB green CSS custom properties
        const root = document.documentElement;
        root.style.setProperty('--gmb-green', '#075E54');
        root.style.setProperty('--gmb-green-light', '#128C7E');
        root.style.setProperty('--gmb-green-pale', '#e8f5e9');
        root.style.setProperty('--gmb-green-rgb', '7, 94, 84');
        // Also set theme-color for compatibility
        root.style.setProperty('--theme-color', '#075E54');
        root.style.setProperty('--theme-color-rgb', '7, 94, 84');
        root.style.setProperty('--theme-color-dark', '#064e3b');

        return () => {
            document.body.classList.remove('login-page');
            root.style.removeProperty('--gmb-green');
            root.style.removeProperty('--gmb-green-light');
            root.style.removeProperty('--gmb-green-pale');
            root.style.removeProperty('--gmb-green-rgb');
            root.style.removeProperty('--theme-color');
            root.style.removeProperty('--theme-color-rgb');
            root.style.removeProperty('--theme-color-dark');
        };
    }, [theme]);

    // Step 1: Email Form
    const emailFormik = useFormik({
        initialValues: {
            email: '',
            user_role: 'Owner',
        },
        validationSchema: Yup.object({
            email: Yup.string().email('Please Enter Valid Email').required('Please Enter Your Email'),
            user_role: Yup.string().required('Please Select User Role'),
        }),
        onSubmit: async (values) => {
            console.log(values);

            try {
                setloder(true);
                const formdata = new FormData();
                formdata.append("email", values.email);
                formdata.append("user_role", values.user_role);

                const requestOptions = {
                    method: "POST",
                    body: formdata,
                    redirect: "follow"
                };

                const response = await fetch(`${domain()}/forgot-password/send-otp`, requestOptions);
                const result = await response.json();

                if (response.ok && result.status) {
                    setEmail(values.email);
                    setUserRole(values.user_role); // Store user role
                    setCurrentStep(2);
                    Swalaleart({
                        title: "OTP Sent",
                        text: "Please check your email for the OTP",
                        icon: "success",
                        confirmcolor: '#1fee5b'
                    });
                } else {
                    Swalaleart({
                        title: "Error",
                        text: result.message || "Failed to send OTP",
                        icon: "error"
                    });
                }
            } catch (error) {
                console.error(error);
                Swalaleart({
                    title: "Error",
                    text: "Network Error",
                    icon: "error"
                });
            } finally {
                setloder(false);
            }
        }
    });

    // Step 2: OTP Form
    const otpFormik = useFormik({
        initialValues: {
            otp: '',
        },
        validationSchema: Yup.object({
            otp: Yup.string().required('Please Enter OTP').length(6, 'OTP must be 6 digits'),
        }),
        onSubmit: async (values) => {
            try {
                setloder(true);
                const formdata = new FormData();
                formdata.append("email", email);
                formdata.append("otp", values.otp);
                formdata.append("user_role", userRole); // Use stored user role

                const requestOptions = {
                    method: "POST",
                    body: formdata,
                    redirect: "follow"
                };

                const response = await fetch(`${domain()}/forgot-password/verify-otp`, requestOptions);
                const result = await response.json();

                if (response.ok && result.status) {
                    setOtp(values.otp);
                    setResetToken(result.data); // Store the token from backend
                    setCurrentStep(3);
                    Swalaleart({
                        title: "OTP Verified",
                        text: "Please enter your new password",
                        icon: "success",
                        confirmcolor: '#1fee5b'
                    });
                } else {
                    Swalaleart({
                        title: "Error",
                        text: result.message || "Invalid OTP",
                        icon: "error"
                    });
                }
            } catch (error) {
                console.error(error);
                Swalaleart({
                    title: "Error",
                    text: "Network Error",
                    icon: "error"
                });
            } finally {
                setloder(false);
            }
        }
    });

    // Step 3: New Password Form
    const passwordFormik = useFormik({
        initialValues: {
            password: '',
            password_confirmation: '',
        },
        validationSchema: Yup.object({
            password: Yup.string()
                .min(8, 'Password must be at least 8 characters')
                .required('Please Enter New Password'),
            password_confirmation: Yup.string()
                .oneOf([Yup.ref('password'), null], 'Passwords must match')
                .required('Please Confirm Password'),
        }),
        onSubmit: async (values) => {
            try {
                setloder(true);
                const formdata = new FormData();
                formdata.append("token", resetToken); // Use the token from backend
                formdata.append("password", values.password);
                formdata.append("password_confirmation", values.password_confirmation);

                const requestOptions = {
                    method: "POST",
                    body: formdata,
                    redirect: "follow"
                };

                const response = await fetch(`${domain()}/forgot-password/reset`, requestOptions);
                const result = await response.json();

                if (response.ok && result.status) {
                    Swalaleart({
                        title: "Success",
                        text: "Password reset successfully! Please login with your new password",
                        icon: "success",
                        confirmcolor: '#1fee5b'
                    });
                    navigate('/');
                } else {
                    Swalaleart({
                        title: "Error",
                        text: result.message || "Failed to reset password",
                        icon: "error"
                    });
                }
            } catch (error) {
                console.error(error);
                Swalaleart({
                    title: "Error",
                    text: "Network Error",
                    icon: "error"
                });
            } finally {
                setloder(false);
            }
        }
    });

    const renderStepIndicator = () => (
        <div className="forgot-password-step-indicator">
            <div className={`forgot-password-step ${currentStep >= 1 ? (currentStep > 1 ? 'completed' : 'active') : 'pending'}`}>
                <div className={`forgot-password-step-number ${currentStep >= 1 ? (currentStep > 1 ? 'completed' : 'active') : 'pending'}`}>
                    {currentStep > 1 ? <FontAwesomeIcon icon={faCheck} /> : '1'}
                </div>
                <span className={`forgot-password-step-label ${currentStep >= 1 ? (currentStep > 1 ? 'completed' : 'active') : 'pending'}`}>Email</span>
            </div>
            <div className={`forgot-password-step ${currentStep >= 2 ? (currentStep > 2 ? 'completed' : 'active') : 'pending'}`}>
                <div className={`forgot-password-step-number ${currentStep >= 2 ? (currentStep > 2 ? 'completed' : 'active') : 'pending'}`}>
                    {currentStep > 2 ? <FontAwesomeIcon icon={faCheck} /> : '2'}
                </div>
                <span className={`forgot-password-step-label ${currentStep >= 2 ? (currentStep > 2 ? 'completed' : 'active') : 'pending'}`}>OTP</span>
            </div>
            <div className={`forgot-password-step ${currentStep >= 3 ? 'active' : 'pending'}`}>
                <div className={`forgot-password-step-number ${currentStep >= 3 ? 'active' : 'pending'}`}>
                    3
                </div>
                <span className={`forgot-password-step-label ${currentStep >= 3 ? 'active' : 'pending'}`}>Password</span>
            </div>
        </div>
    );

    const renderEmailStep = () => (
        <form onSubmit={emailFormik.handleSubmit} className="forgot-password-form">
            <div className="forgot-password-form-group">
                <div className="forgot-password-input-group">
                    <div className="forgot-password-input-icon">
                        <FontAwesomeIcon icon={faEnvelope} />
                    </div>
                    <input
                        type="email"
                        className="forgot-password-input"
                        onChange={emailFormik.handleChange}
                        onBlur={emailFormik.handleBlur}
                        value={emailFormik.values.email}
                        name='email'
                        placeholder="Enter Email"
                    />
                </div>
                {emailFormik.errors.email && emailFormik.touched.email && (
                    <div className="forgot-password-error">
                        {emailFormik.errors.email}
                    </div>
                )}
            </div>
            <div className="forgot-password-form-group">
                <div className="forgot-password-input-group">
                    <div className="forgot-password-input-icon">
                        <FontAwesomeIcon icon={faUser} />
                    </div>
                    <select
                        className="forgot-password-select"
                        onChange={emailFormik.handleChange}
                        onBlur={emailFormik.handleBlur}
                        value={emailFormik.values.user_role}
                        name='user_role'
                        id="userRole"
                    >
                        <option value="Owner">Owner</option>
                        <option value="Agent">Agent</option>
                    </select>
                </div>
                {emailFormik.errors.user_role && emailFormik.touched.user_role && (
                    <div className="forgot-password-error">
                        {emailFormik.errors.user_role}
                    </div>
                )}
            </div>
            <button
                type='submit'
                className='forgot-password-btn forgot-password-btn-primary'
            >
                Send OTP
            </button>
        </form>
    );

    const renderOtpStep = () => (
        <form onSubmit={otpFormik.handleSubmit} className="forgot-password-form">
            <div className="forgot-password-form-group">
                <div className="forgot-password-input-group">
                    <div className="forgot-password-input-icon">
                        <FontAwesomeIcon icon={faUser} />
                    </div>
                    <input
                        type="text"
                        className="forgot-password-input"
                        onChange={otpFormik.handleChange}
                        onBlur={otpFormik.handleBlur}
                        value={otpFormik.values.otp}
                        name='otp'
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                    />
                </div>
                {otpFormik.errors.otp && otpFormik.touched.otp && (
                    <div className="forgot-password-error">
                        {otpFormik.errors.otp}
                    </div>
                )}
            </div>
            <button
                type='submit'
                className='forgot-password-btn forgot-password-btn-primary'
            >
                Verify OTP
            </button>
            <button
                type="button"
                className='forgot-password-btn forgot-password-btn-secondary'
                onClick={() => setCurrentStep(1)}
            >
                Back to Email
            </button>
        </form>
    );

    const renderPasswordStep = () => (
        <form onSubmit={passwordFormik.handleSubmit} className="forgot-password-form">
            <div className="forgot-password-form-group">
                <div className="forgot-password-input-group">
                    <div className="forgot-password-input-icon">
                        <FontAwesomeIcon icon={faLock} />
                    </div>
                    <input
                        type={showPassword ? "text" : "password"}
                        className="forgot-password-input"
                        onChange={passwordFormik.handleChange}
                        onBlur={passwordFormik.handleBlur}
                        value={passwordFormik.values.password}
                        name='password'
                        placeholder="Enter New Password"
                    />
                    <button
                        type="button"
                        className="forgot-password-password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                    >
                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </button>
                </div>
                {passwordFormik.errors.password && passwordFormik.touched.password && (
                    <div className="forgot-password-error">
                        {passwordFormik.errors.password}
                    </div>
                )}
            </div>
            <div className="forgot-password-form-group">
                <div className="forgot-password-input-group">
                    <div className="forgot-password-input-icon">
                        <FontAwesomeIcon icon={faLock} />
                    </div>
                    <input
                        type={showConfirmPassword ? "text" : "password"}
                        className="forgot-password-input"
                        onChange={passwordFormik.handleChange}
                        onBlur={passwordFormik.handleBlur}
                        value={passwordFormik.values.password_confirmation}
                        name='password_confirmation'
                        placeholder="Confirm New Password"
                    />
                    <button
                        type="button"
                        className="forgot-password-password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                        <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                    </button>
                </div>
                {passwordFormik.errors.password_confirmation && passwordFormik.touched.password_confirmation && (
                    <div className="forgot-password-error">
                        {passwordFormik.errors.password_confirmation}
                    </div>
                )}
            </div>
            <button
                type='submit'
                className='forgot-password-btn forgot-password-btn-primary'
            >
                Reset Password
            </button>
            <button
                type="button"
                className='forgot-password-btn forgot-password-btn-secondary'
                onClick={() => setCurrentStep(2)}
            >
                Back to OTP
            </button>
        </form>
    );

    const getStepTitle = () => {
        switch (currentStep) {
            case 1: return "Forgot Password";
            case 2: return "Enter OTP";
            case 3: return "Reset Password";
            default: return "Forgot Password";
        }
    };

    const getStepSubtitle = () => {
        switch (currentStep) {
            case 1: return "Enter your email to receive OTP";
            case 2: return "Enter the 6-digit OTP sent to your email";
            case 3: return "Enter your new password";
            default: return "Enter your email to receive OTP";
        }
    };

    return (
        <div className={`forgot-password-container ${loder ? 'forgot-password-loading' : ''}`}>
            <ToastContainer newestOnTop={false} rtl={false} pauseOnFocusLoss />
            <Preloader action={loder} />
            <div className="forgot-password-form-wrapper">
                <div className="forgot-password-form-card">
                    <img src={Logo} alt="logo" className="forgot-password-logo" />
                    <h4 className="forgot-password-title">{getStepTitle()}</h4>
                    <h6 className="forgot-password-subtitle">{getStepSubtitle()}</h6>
                    {renderStepIndicator()}
                    {currentStep === 1 && renderEmailStep()}
                    {currentStep === 2 && renderOtpStep()}
                    {currentStep === 3 && renderPasswordStep()}
                    <div className="forgot-password-login-link">
                        <span>Remember your password? </span>
                        <Link to={'/'}>Login</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
